// Selección múltiple en la tabla de un lanzamiento —
// ADR-2026-09-08-acciones-en-lote.
//
// La barra aparece SOBRE la tabla y solo cuando hay algo seleccionado. Es una
// barra flotante y no una fila más de la tabla porque las acciones son de la
// selección, no del lanzamiento. Desde ADR-2026-09-30-lo-tildado-es-lo-que-se-publica
// lo tildado es además lo que se publica, y arranca todo tildado: la barra se ve de
// entrada y dice cuántos van a salir.
import { boton } from './botones';
import { FolderPlus, Loader2, Sparkles, Tag, Trash2, X } from 'lucide-react';
import { SheetLateral } from '../../components/SheetLateral';
import { ArbolDeCategorias } from './Categorias';
import type { CategoriaDeTienda } from './lanzamientosApi';

export function BarraDeSeleccion({
  cantidad,
  total,
  aLaVista,
  verbo,
  onLimpiar,
  onCompletarConIa,
  onCategorizar,
  onBorrar,
}: {
  cantidad: number;
  /** Cuántos productos tiene el lanzamiento: lo tildado es lo que se publica. */
  total: number;
  /**
   * Cuántos de los tildados se ven con la búsqueda puesta: a ésos van las acciones
   * de la barra. Publicar sigue yendo a los `cantidad`.
   */
  aLaVista: number;
  /** Qué hace el botón principal con lo tildado. */
  verbo: 'publicar' | 'descargar';
  onLimpiar: () => void;
  onCompletarConIa: () => void;
  onCategorizar: () => void;
  onBorrar: () => void;
}) {
  // Si la búsqueda esconde tildados, la barra lo dice: el número de arriba es lo que se
  // publica, y las acciones de al lado van sólo a lo que se ve.
  const escondidos = aLaVista !== cantidad;
  const sinAcciones = aLaVista === 0;
  return (
    <div
      data-tour="tienda-lote"
      className="flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-[rgb(var(--brand-primary-rgb)_/_0.03)] py-2.5 px-4"
      style={{ animation: 'bulk-dock-in 0.25s ease-out' }}
    >
      <span className="text-sm font-medium text-brand-black">
        {cantidad} de {total} para {verbo}
      </span>
      <button
        type="button"
        onClick={onLimpiar}
        aria-label="Destildar todos"
        title="Destildar todos"
        className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-200 hover:text-brand-black"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {escondidos && (
        <span className="text-xs text-neutral-500">
          {sinAcciones
            ? 'Ninguno tildado entre los que se ven'
            : aLaVista === 1
              ? 'Las acciones van al que se ve'
              : `Las acciones van a los ${aLaVista} que se ven`}
        </span>
      )}

      {/* Teléfono: las tres acciones en una fila de botones iguales, ícono arriba y texto abajo.
          Como pastillas se partían en dos renglones y "Sacar del lanzamiento" quedaba sola
          abajo (ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono). */}
      <span className="grid w-full grid-cols-3 gap-2 sm:hidden">
        {/* "Completar" a secas: con "con IA" no entraba en un tercio; las chispas ya dicen IA. */}
        <AccionDeLote
          onClick={onCompletarConIa}
          disabled={sinAcciones}
          aria-label="Completar con IA"
          icono={<Sparkles className="h-4 w-4" />}
        >
          Completar
        </AccionDeLote>
        <AccionDeLote onClick={onCategorizar} disabled={sinAcciones} icono={<FolderPlus className="h-4 w-4" />}>
          Categoría
        </AccionDeLote>
        <AccionDeLote
          onClick={onBorrar}
          disabled={sinAcciones}
          peligro
          aria-label="Sacar del lanzamiento"
          icono={<Trash2 className="h-4 w-4" />}
        >
          Sacar
        </AccionDeLote>
      </span>
      <span className="ml-auto hidden flex-wrap items-center gap-2 sm:flex">
        <button
          type="button"
          onClick={onCompletarConIa}
          disabled={sinAcciones}
          className={boton('contorno', { chico: true, extra: 'py-2' })}
        >
          <Sparkles className="w-3.5 h-3.5" /> Completar con IA
        </button>
        <button
          type="button"
          onClick={onCategorizar}
          disabled={sinAcciones}
          className={boton('contorno', { chico: true, extra: 'py-2' })}
        >
          <FolderPlus className="w-3.5 h-3.5" /> Categoría
        </button>
        <button
          type="button"
          onClick={onBorrar}
          disabled={sinAcciones}
          className={boton('contornoPeligro', { chico: true, extra: 'py-2' })}
        >
          <Trash2 className="w-3.5 h-3.5" /> Sacar del lanzamiento
        </button>
      </span>
    </div>
  );
}

/** Un botón de la fila de acciones en lote del teléfono. */
function AccionDeLote({
  onClick,
  disabled,
  peligro = false,
  icono,
  children,
  'aria-label': ariaLabel,
}: {
  onClick: () => void;
  disabled: boolean;
  peligro?: boolean;
  icono: React.ReactNode;
  children: React.ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl py-2.5 px-1 text-xs font-medium transition-colors active:brightness-95 disabled:opacity-40 ${
        peligro ? 'bg-white text-red-600 shadow-sm ring-1 ring-red-200' : 'bg-white text-brand-primary shadow-sm ring-1 ring-[rgb(var(--brand-primary-rgb)_/_0.2)]'
      }`}
    >
      {icono}
      <span className="max-w-full truncate">{children}</span>
    </button>
  );
}

/**
 * Elegir a qué categoría van los productos seleccionados.
 *
 * Es el MISMO árbol que el panel donde se editan las categorías —indentado,
 * plegable, con el botón para crear una subcategoría adentro de cualquier fila y
 * el de crear una de primer nivel abajo—. Lo único que cambia es qué hace un
 * click en una fila: allá la renombra, acá la aplica a la selección. Una lista
 * distinta para lo mismo obligaría al usuario a aprender dos veces dónde está
 * cada cosa.
 *
 * La categoría se AGREGA, no reemplaza. Un producto pertenece a varias, y
 * borrarle las que ya tenía porque acá se eligió otra sería destruir trabajo sin
 * avisar y sin forma de deshacerlo.
 */
export function SheetDeCategoria({
  abierto,
  cantidad,
  categorias,
  trabajando,
  error,
  onCerrar,
  onElegir,
  onCrearYElegir,
}: {
  abierto: boolean;
  cantidad: number;
  categorias: CategoriaDeTienda[];
  trabajando: boolean;
  error: string | null;
  onCerrar: () => void;
  onElegir: (id: string) => void;
  /** Crea la categoría en el lugar del árbol donde se apretó, y la aplica. */
  onCrearYElegir: (nombre: string, madre: string | null) => void;
}) {
  return (
    <SheetLateral
      open={abierto}
      onClose={onCerrar}
      titulo={`Categoría para ${cantidad} producto${cantidad === 1 ? '' : 's'}`}
    >
      <div className="p-4">
        <p className="text-xs leading-relaxed text-neutral-500">
          Tocá una para <strong className="font-medium text-brand-black">agregársela</strong> a
          los {cantidad} productos. Las que ya tengan se quedan como están.
        </p>

        {error && (
          <p className="mt-3 rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{error}</p>
        )}

        <div className="mt-3">
          <ArbolDeCategorias
            categorias={categorias}
            deshabilitado={trabajando}
            onCrear={async (nombre, madre) => onCrearYElegir(nombre, madre)}
            vacio={
              <div className="rounded-2xl border border-dashed border-neutral-300 py-10 px-4 text-center">
                <Tag className="mx-auto w-6 h-6 text-neutral-300" />
                <p className="mt-3 text-sm font-medium text-brand-black">
                  Todavía no tenés ninguna
                </p>
                <p className="mx-auto mt-1 max-w-xs text-xs text-neutral-500">
                  Creá la primera acá abajo y se les agrega a los {cantidad} de una.
                </p>
              </div>
            }
            renderNombre={(c) => (
              <button
                type="button"
                disabled={trabajando}
                onClick={() => onElegir(c.id)}
                className="min-w-0 truncate rounded-lg py-1 px-1 text-left text-sm text-brand-black hover:bg-neutral-100 disabled:opacity-40"
              >
                {c.nombre}
              </button>
            )}
          />
        </div>

        {trabajando && (
          <p className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…
          </p>
        )}
      </div>
    </SheetLateral>
  );
}
